"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Zap, MapPin, PiggyBank, Target } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
interface PriorityPillar {
  id: string;
  badge: string;
  title: string;
  summary: string;
  points: string[];
  timeline: string;
  serviceLink: string;
  serviceLabel: string;
}
const pillars: PriorityPillar[] = [{
  id: "quick-wins",
  badge: "Stage 01",
  title: "Prioritized quick wins",
  summary: "I audit your site directly and isolate the high-impact fixes that can show progress in weeks rather than months.",
  points: ["Fix crawl errors, orphaned pages, and broken redirect chains", "Re-optimize existing pages currently sitting on page 2 (ranks 11–20)", "Improve internal anchor links to boost existing top commercial URLs", "Correct core metadata gaps and duplicate search intent cannibalization"],
  timeline: "First 2 to 4 weeks",
  serviceLink: "/TechnicalSeoPage",
  serviceLabel: "Explore technical fixes"
}, {
  id: "local-fundamentals",
  badge: "Stage 02",
  title: "Local visibility fundamentals",
  summary: "For regional or local service firms, I anchor your presence where customers are already looking.",
  points: ["Complete Google Business Profile audit and primary category alignment", "Local service page architecture tailored to your specific trading postcodes", "Structured schema markup for local business NAP consistency", "Direct guidance on gathering genuine customer reviews systematically"],
  timeline: "Month 1 onwards",
  serviceLink: "/LocalSEOPage",
  serviceLabel: "See local SEO work"
}, {
  id: "budget-conscious",
  badge: "Stage 03",
  title: "Budget-conscious growth strategy",
  summary: "No agency retainers that bill you for account managers. Every hour is spent solely on search actions that move the needle.",
  points: ["A lean, transparent action plan with clear deliverables each cycle", "Zero fluff content creation; we target high-intent commercial keywords first", "Direct advice on what NOT to spend money on right now", "You speak directly with me on every call, with full access to my work"],
  timeline: "Ongoing partnership",
  serviceLink: "/ContactPage",
  serviceLabel: "Discuss your roadmap"
}];
export default function Priorities() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="strategicpriorities" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border transition-colors">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 md:mb-18">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-medium tracking-normal text-xs uppercase">
            My Working Approach
          </Badge>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground font-default mb-4">
            Where I start when every pound needs to count
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            When you hire me, you get a clear sequence. I tackle immediate ranking blockers first, lock in local search visibility, and build momentum without burning budget on low-impact work.
          </p>
        </div>

        {/* 3 Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-14">
          {pillars.map((pillar, i) => <motion.div key={pillar.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: i * 0.12
        }} className="flex flex-col h-full">
              <Card className="h-full flex flex-col justify-between border border-border bg-card shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="text-xs font-semibold tracking-wide uppercase text-primary">
                        {pillar.badge}
                      </span>
                      <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-sm">
                        {pillar.timeline}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-semibold tracking-tight text-card-foreground font-default">
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {pillar.summary}
                    </p>
                    <ul className="space-y-2.5 pt-2 border-t border-border/60">
                      {pillar.points.map((point, pIdx) => <li key={pIdx} data-index={pIdx} className="text-xs md:text-sm text-card-foreground flex items-start gap-2.5 leading-snug">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </div>
                <div className="p-6 pt-0 mt-4 border-t border-border/40">
                  <Button variant="link" asChild className="p-0 h-auto font-medium text-primary hover:text-primary/80 group text-sm">
                    <Link to={pillar.serviceLink} className="inline-flex items-center gap-1.5">
                      <span>{pillar.serviceLabel}</span>
                      <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </Button>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom Banner with Direct Access Guarantee */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} className="rounded-xl border border-border bg-card p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-xs">
          <div className="space-y-1.5 max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="inline-flex size-2 rounded-full bg-primary" />
              <h3 className="text-base font-semibold text-foreground">
                You work directly with me, never a junior coordinator
              </h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Every audit, technical recommendation, and strategy review is handled by Yassine Essaadi. No account handlers, no outsourced work, and no generic monthly retainers.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 w-full md:w-auto">
            <Button asChild className="w-full md:w-auto font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}